import { useState, useEffect, Fragment } from 'react';
import { apiFetch } from '../api/client';
import { TrendingUp, Minus, ChevronDown, ChevronRight, Activity } from 'lucide-react';

// ── Types ──────────────────────────────────────────────────────────────────

interface TriageAlarm {
  severity_band: string | null;
  alarm_level: number | null;
  value_inps: number | null;
  threshold_inps: number | null;
  threshold_band: string | null;     // 'warning' | 'alert'
  threshold_source: string | null;   // 'override' | 'statistical'
  fired_at: string | null;
  created_at: string | null;
  days_active: number | null;
  capture_linkage: string;
  onset_isolable: boolean | null;
  unreconstructed_reason: string | null;
}

interface TriageConcentration {
  // 'computed' = concentration pipeline ran for this capture (ratio may still be
  // null → genuine diffuse). 'not_computed' = no velocity spectrum was ever
  // computed for this axis (e.g. MQTT-only). Older payloads omit state.
  state?: 'computed' | 'not_computed';
  ratio: number | null;
  band: string | null;
  cut_mode: string | null;
}

interface TriageComb {
  sync_defined: boolean;
  n_harmonics: number | null;
  sync_energy_mmps: number | null;
}

interface TriageHarmonics {
  status: string;                       // 'yes' | 'yes_lower_fundamental' | 'yes_unrelated' | 'no' | 'not_evaluated'
  count: number | null;
  family_fundamental_cpm: number | null;
  peak_k: number | null;
  peak_cpm: number | null;
  members: number[] | null;
  rejected_by: string | null;
}

interface TriageIdentity {
  dominant_order: number | null;   // peak-anchored harmonic index (k) | null
  order_source: string | null;     // 'peak_anchored' | null (D-03)
  rpm_tier: string | null;
  hp_compromised: boolean | null;
  crossover_flag: boolean | null;
  capture_rpm: number | null;
}

interface TriageROCEntry {
  order_k_cpm: number;
  slope: number;
  window_days: number;
  n_captures: number;
  hp_compromised: boolean;
}

interface TriageDominantLine {
  freq_cpm: number | null;
  amplitude_ins: number | null;               // in/s (display unit)
  amplitude_mmps: number | null;              // native (reference)
  present_fraction: number | null;
  amplitude_roc_ins_per_week: number | null;  // in/s/week, positive=rising; null=stable/falling
  anchor: string | null;                      // 'violating' | 'near_fire' | 'latest_capture' | null
  anchor_note: string | null;
  eligibility: string | null;                 // 'active'|'above_ceiling'|'below_floor'|'not_evaluated'
  n_captures: number | null;
  n_total: number | null;
  window_days: number | null;
  first_seen: string | null;
  last_seen: string | null;
  alarm_line_label: string | null;
  routing_hint: string;
}

interface TriageCurrentSpectrum {
  freq_cpm: number | null;
  amplitude_ins: number | null;   // in/s
  last_seen: string | null;
  hf_dominant: boolean;
  below_floor: boolean;
  // above-ceiling disclosure (independent of the in-band trended line)
  hf_freq_cpm: number | null;
  hf_amplitude_ins: number | null;   // in/s pk
  hf_last_seen: string | null;
}

interface TriageRow {
  alarm_id: number;
  machine_id: string;
  machine_name: string;
  point_index: number;
  point_name: string | null;
  axis_name: string;
  company_name: string;
  area_name: string;
  db_id: number;
  resolved: boolean;
  vel_display: { factor: number; label: string };
  alarm: TriageAlarm;
  concentration: TriageConcentration;
  comb: TriageComb;
  identity: TriageIdentity;
  roc: TriageROCEntry[];
  roc_status?: string;              // 'pending_recompute' (D-04) — slopes suppressed
  dominant_line: TriageDominantLine | null;
  current_spectrum: TriageCurrentSpectrum | null;
  harmonics: TriageHarmonics | null;
  routing_class: string;
  verdict: string | null;
}

// ── Display constants ──────────────────────────────────────────────────────

const SEVERITY_STYLES: Record<string, string> = {
  severe:   'bg-red-100 text-red-700 border-red-200',
  moderate: 'bg-amber-100 text-amber-700 border-amber-200',
};

const CONC_STYLES: Record<string, string> = {
  dominant:     'text-red-600 font-semibold',
  concentrated: 'text-amber-600 font-medium',
  moderate:     'text-gray-700',
  diffuse:      'text-gray-400',
};

const RPM_TIER_BADGES: Record<string, { symbol: string; title: string; cls: string }> = {
  corroborated:  { symbol: '●', title: 'corroborated',  cls: 'text-teal-600' },
  single_method: { symbol: '◐', title: 'single method', cls: 'text-amber-500' },
  unresolved:    { symbol: '○', title: 'unresolved',    cls: 'text-gray-400' },
};

// ── Helpers ────────────────────────────────────────────────────────────────

// Amplitude convention. Canonical spectral lines are PEAK; alarm/overall values
// are RMS (backend vel_display.label = "in/s RMS"). Spectral amplitudes (dominant
// line, current spectrum) are labelled "in/s pk" so the two are never compared as
// if equal. The pk<->RMS factor itself lives in canonical_spectrum (peak_to_rms).
const SPEC_PK_LABEL = 'in/s pk';

// The ONE mm/s -> in/s conversion for this component (mirrors backend
// canonical_spectrum.INS_PER_MMPS). No inline /25.4 anywhere else.
const MM_TO_IN = 1 / 25.4;
const mmpsToIns = (x: number | null | undefined): number | null =>
  (x === null || x === undefined) ? null : x * MM_TO_IN;

// Anchor-provenance markers for the alarm-line cell. 'violating' is the alarm's
// own line (no marker); the fallbacks must never imply they fired the alarm.
const ANCHOR_MARKERS: Record<string, { symbol: string; title: string; cls: string }> = {
  near_fire:      { symbol: '≈', title: 'nearest capture to alarm; violating capture linkage unavailable', cls: 'text-gray-400' },
  latest_capture: { symbol: '·', title: 'latest capture on axis; not linked to the violating captures',     cls: 'text-gray-400' },
};

/** Parse YYYY/MM/DD HH:MM:SS → "Jun 22" */
function fmtFiredAt(fired_at: string | null): string {
  if (!fired_at) return '—';
  const [datePart] = fired_at.split(' ');
  const parts = datePart.split('/');
  if (parts.length < 3) return fired_at;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const m = parseInt(parts[1]) - 1;
  const d = parseInt(parts[2]);
  return `${months[m] ?? '?'} ${d}`;
}

function fmtDominantLine(dl: TriageDominantLine | null | undefined): React.ReactNode {
  if (!dl || dl.freq_cpm === null) {
    return <span className="text-gray-300 text-xs">—</span>;
  }
  const freqLabel = dl.freq_cpm >= 10_000
    ? `${(dl.freq_cpm / 1000).toFixed(1)}k`
    : `${Math.round(dl.freq_cpm)}`;
  // amplitude already in/s from the backend (canonical helper) — no local /25.4.
  // Spectral line amplitude is PEAK → labelled "in/s pk", not the alarm's RMS.
  const ampLabel = dl.amplitude_ins !== null
    ? `${dl.amplitude_ins.toFixed(3)} ${SPEC_PK_LABEL}`
    : null;
  const rising = dl.amplitude_roc_ins_per_week !== null && dl.amplitude_roc_ins_per_week > 0;
  const isNew  = dl.routing_hint === 'new_line'
    || (dl.present_fraction !== null && dl.present_fraction < 0.15);
  const anchorMark = dl.anchor && dl.anchor !== 'violating' ? ANCHOR_MARKERS[dl.anchor] : null;
  return (
    <div className="flex flex-col gap-0.5 min-w-[110px]">
      <div className="flex items-center gap-1 flex-wrap">
        {rising
          ? <span title={`+${dl.amplitude_roc_ins_per_week!.toFixed(5)} ${SPEC_PK_LABEL}/wk`} className="inline-flex"><TrendingUp className="w-3 h-3 text-amber-500 shrink-0" /></span>
          : <span title="stable or declining" className="inline-flex"><Minus className="w-3 h-3 text-gray-300 shrink-0" /></span>
        }
        <span className="font-mono text-gray-800 text-xs">{freqLabel} CPM</span>
        {anchorMark && (
          <span className={`text-[11px] font-semibold ${anchorMark.cls}`} title={anchorMark.title}>
            {anchorMark.symbol}
          </span>
        )}
        {dl.alarm_line_label && (
          <span className="px-1 rounded text-[9px] bg-gray-100 text-gray-600 border border-gray-200 font-mono leading-tight"
                title="alarm-anchored line label">
            {dl.alarm_line_label}
          </span>
        )}
        {isNew && (
          <span className="px-1 rounded text-[9px] bg-blue-50 text-blue-600 border border-blue-200 font-semibold leading-tight">
            NEW
          </span>
        )}
        {dl.eligibility === 'above_ceiling' && (
          <span className="px-1 rounded text-[9px] bg-purple-50 text-purple-600 border border-purple-200 leading-tight">
            HF
          </span>
        )}
      </div>
      {ampLabel && (
        <span className="font-mono text-gray-600 text-[11px]">{ampLabel}</span>
      )}
      <div className="flex items-center gap-1.5 flex-wrap">
        {dl.present_fraction !== null && (
          <span className="text-gray-400 text-[10px]" title="dominant in N% of captures over the window">
            dominant in {Math.round(dl.present_fraction * 100)}%
          </span>
        )}
      </div>
    </div>
  );
}

// ── Props ──────────────────────────────────────────────────────────────────

interface Props {
  onMachineClick?: (machineId: string, machineName: string) => void;
}

// ── Component ──────────────────────────────────────────────────────────────

export function VelocityTriage({ onMachineClick }: Props) {
  const [rows, setRows]         = useState<TriageRow[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [days, setDays]         = useState(7);
  const [includeResolved, setIncludeResolved] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    setLoading(true);
    setError('');
    const q = `/velocity-triage?days=${days}${includeResolved ? '&include_resolved=true' : ''}`;
    apiFetch<TriageRow[]>(q)
      .then(data => { setRows(data || []); setLoading(false); })
      .catch(err  => { setError(err?.message || 'Failed to load'); setLoading(false); });
  }, [days, includeResolved]);

  const toggleExpand = (id: number) =>
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // ── loading / error / empty ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm h-64 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm h-64 flex items-center justify-center text-red-500 text-sm">{error}</div>
    );
  }

  const velLabel = rows[0]?.vel_display?.label ?? 'in/s RMS';

  return (
    <div className="space-y-5">
      {/* header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
            <Activity className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Velocity Alarm Triage</h1>
            <p className="text-sm text-gray-500">
              {rows.filter(r => !r.resolved).length} active alarm{rows.filter(r => !r.resolved).length !== 1 ? 's' : ''}
              {includeResolved && rows.some(r => r.resolved)
                ? ` · ${rows.filter(r => r.resolved).length} recently resolved` : ''}
              {' '}· recency window {days}d · values in <span className="font-medium text-gray-700">{velLabel}</span>
              {' '}· no fault-type assignments
            </p>
          </div>
        </div>
        {/* recency window picker */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-gray-500">Window:</span>
          {[7, 14, 30].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                days === d
                  ? 'bg-gray-800 text-white border-gray-800'
                  : 'text-gray-600 border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              {d}d
            </button>
          ))}
          {/* Show recently resolved (default OFF) */}
          <button
            onClick={() => setIncludeResolved(v => !v)}
            title="Also show alarms that resolved within the window"
            className={`ml-2 text-xs px-2.5 py-1 rounded-full border transition-all ${
              includeResolved
                ? 'bg-teal-600 text-white border-teal-600'
                : 'text-gray-600 border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            {includeResolved ? '✓ ' : ''}Recently resolved
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm h-64 flex flex-col items-center justify-center text-gray-400 gap-2">
          <span className="text-lg font-medium">No active velocity alarms</span>
          <span className="text-sm text-gray-300">within the {days}-day window</span>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 w-6" />
              {/* Col 1 */}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Machine / Point</th>
              {/* Col 2 */}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Alarm ({velLabel})</th>
              {/* Col 2b: Alarm Line — alarm-anchored dominant spectral line */}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Alarm Line</th>
              {/* Col 3 */}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Concentration</th>
              {/* Col 4 */}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Harmonics</th>
              {/* Col 5 */}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Speed</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(row => {
              const isOpen  = expanded.has(row.alarm_id);
              const { alarm, concentration, identity, dominant_line, harmonics } = row;

              // ── Col 2: Alarm ───────────────────────────────────────────
              const sevStyle = alarm.severity_band
                ? (SEVERITY_STYLES[alarm.severity_band] ?? 'bg-gray-100 text-gray-600 border-gray-200')
                : '';

              // Date display
              const dateStr = fmtFiredAt(alarm.fired_at);
              const dateLabel = alarm.onset_isolable === false
                ? `in continuous violation since ~${dateStr}`
                : dateStr;

              // Threshold line: "0.18 in/s alert (override)"
              const thrLine = alarm.threshold_inps !== null
                ? `${alarm.threshold_inps.toFixed(3)} ${velLabel} ${alarm.threshold_band ?? ''}${alarm.threshold_source === 'override' ? ' (override)' : ''}`
                : null;

              // ── Col 3: Concentration ────────────────────────────────────
              let concNode: React.ReactNode;
              if (concentration.state === 'not_computed') {
                // never computed — do NOT render as diffuse (that would assert an
                // empty spectrum was measured). Show an explicit not-yet-computed dash.
                concNode = (
                  <span
                    className="text-gray-300 text-xs cursor-help"
                    title="spectral concentration not yet computed for this capture"
                  >
                    —
                  </span>
                );
              } else if (concentration.ratio === null) {
                // computed, but no discrete lines above prominence → genuine diffuse
                concNode = (
                  <span className="text-gray-400 italic text-xs">diffuse — no dominant lines</span>
                );
              } else {
                const band = concentration.band ?? 'diffuse';
                concNode = (
                  <div className="flex flex-col gap-0.5">
                    <span className={CONC_STYLES[band] ?? 'text-gray-600'}>{band}</span>
                    <span className="text-gray-400 text-[10px]">
                      {(concentration.ratio * 100).toFixed(0)}%
                      {concentration.cut_mode === 'hp_floor' && (
                        <span className="ml-1 text-gray-300">·hp</span>
                      )}
                    </span>
                  </div>
                );
              }

              // ── Col 4: Speed tier ────────────────────────────────────────
              const tierInfo = identity.rpm_tier
                ? (RPM_TIER_BADGES[identity.rpm_tier] ?? null)
                : null;

              return (
                <Fragment key={row.alarm_id}>
                  <tr
                    onClick={() => toggleExpand(row.alarm_id)}
                    className={`cursor-pointer transition-colors hover:bg-gray-50 ${row.resolved ? 'opacity-60' : ''}`}
                  >
                    <td className="px-4 py-3 text-gray-300">
                      {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    </td>

                    {/* Col 1: Machine / Point */}
                    <td className="px-4 py-3">
                      <button
                        className="text-left hover:underline"
                        onClick={e => { e.stopPropagation(); onMachineClick?.(row.machine_id, row.machine_name); }}
                      >
                        <div className="text-gray-900 font-medium text-sm leading-tight">{row.machine_name}</div>
                        <div className="text-gray-400 text-xs mt-0.5">
                          {row.point_name ?? `P${row.point_index}`} · {row.axis_name}
                        </div>
                      </button>
                    </td>

                    {/* Col 2: Alarm */}
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5 min-w-[140px]">
                        {row.resolved ? (
                          <span className="self-start px-1.5 py-0.5 rounded text-[10px] font-medium border bg-gray-100 text-gray-500 border-gray-200"
                                title="alarm resolved (recovered); date is the alarm's fire date — resolve time is not tracked">
                            resolved · {dateStr}
                          </span>
                        ) : alarm.severity_band && (
                          <span className={`self-start px-1.5 py-0.5 rounded text-[10px] font-semibold border ${sevStyle}`}>
                            {alarm.severity_band}
                          </span>
                        )}
                        {alarm.value_inps !== null && (
                          <span className="font-mono text-gray-800 text-xs font-semibold">
                            {alarm.value_inps.toFixed(3)}
                            <span className="font-sans text-gray-400 font-normal ml-0.5">{velLabel}</span>
                          </span>
                        )}
                        {thrLine && (
                          <span className="text-gray-400 text-[10px] leading-tight">
                            exceeded {thrLine}
                          </span>
                        )}
                        {alarm.onset_isolable === false ? (
                          <span className="text-amber-600 text-[10px] italic">
                            {dateLabel}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-[10px]">{dateStr}</span>
                        )}
                      </div>
                    </td>

                    {/* Col 2b: Peak Frequency (+ HF disclosure badge, independent of the in-band line) */}
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        {fmtDominantLine(dominant_line)}
                        {row.current_spectrum?.hf_dominant && row.current_spectrum.hf_freq_cpm !== null && (
                          <span
                            className="inline-flex items-center gap-0.5 self-start px-1.5 py-0.5 rounded text-[9px] font-semibold
                                       bg-orange-100 text-orange-700 border border-orange-200 cursor-help"
                            title={`Above-ceiling energy at ${Math.round(row.current_spectrum.hf_freq_cpm)} CPM `
                              + `(${row.current_spectrum.hf_amplitude_ins?.toFixed(3) ?? '?'} in/s pk) — above the velocity `
                              + `tracking ceiling; see acceleration / Impact (PeakVue) for bearing detail.`}
                          >
                            ⤴ HF {(row.current_spectrum.hf_freq_cpm / 1000).toFixed(0)}k
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Col 3: Concentration */}
                    <td className="px-4 py-3">{concNode}</td>

                    {/* Col 4: Harmonics */}
                    <td className="px-4 py-3">
                      {!harmonics || harmonics.status === 'not_evaluated'
                        ? <span className="text-gray-300 text-xs">—</span>
                        : harmonics.status === 'yes'
                          ? (
                            <span className="text-teal-700 text-xs font-medium">
                              Yes·{harmonics.count}×
                            </span>
                          )
                          : harmonics.status === 'yes_lower_fundamental'
                            ? (
                              <span
                                className="text-teal-600 text-xs font-medium cursor-help"
                                title={`Dominant peak is k=${harmonics.peak_k} of f₀ — lower fundamental at ${Math.round(harmonics.family_fundamental_cpm ?? 0)} CPM`}
                              >
                                Yes·{harmonics.count}×@{Math.round(harmonics.family_fundamental_cpm ?? 0)}
                              </span>
                            )
                            : harmonics.status === 'yes_unrelated'
                              ? (
                                <span className="text-gray-400 text-xs italic">
                                  unrelated
                                </span>
                              )
                              : (
                                <span className="text-gray-400 text-xs">No</span>
                              )
                      }
                    </td>

                    {/* Col 5: Speed */}
                    <td className="px-4 py-3">
                      {tierInfo ? (
                        <span className={`text-sm ${tierInfo.cls}`} title={tierInfo.title}>
                          {tierInfo.symbol}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>

                  {/* Expansion slot keyed by alarm_event_id — verdict renders here */}
                  {isOpen && (
                    <tr key={`x-${row.alarm_id}`} className="bg-gray-50/40">
                      <td />
                      <td colSpan={6} className="px-6 py-4">
                        {row.verdict
                          ? <div className="text-gray-700 text-sm">{row.verdict}</div>
                          : <ExpansionDetail row={row} velLabel={velLabel} />
                        }
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Expansion detail ────────────────────────────────────────────────────────

function ExpansionDetail({ row, velLabel }: { row: TriageRow; velLabel: string }) {
  const { alarm, concentration, identity, dominant_line: dl, current_spectrum: cs, harmonics } = row;
  return (
    <div className="grid grid-cols-4 gap-6 text-xs text-gray-600">

      <div>
        <p className="font-semibold text-gray-700 mb-1.5">Alarm</p>
        <dl className="space-y-0.5">
          <KV k="Value"      v={alarm.value_inps !== null ? `${alarm.value_inps.toFixed(4)} ${velLabel}` : '—'} />
          <KV k="Threshold"  v={alarm.threshold_inps !== null
            ? `${alarm.threshold_inps.toFixed(4)} ${velLabel} (${alarm.threshold_band ?? '?'} · ${alarm.threshold_source ?? '?'})`
            : '—'} />
          <KV k="Fired"      v={alarm.fired_at ?? '—'} />
          <KV k="Capture"    v={alarm.capture_linkage} />
          <KV k="Onset"      v={
            alarm.onset_isolable === null ? 'indeterminate'
            : alarm.onset_isolable        ? 'isolable'
                                          : 'continuous violation'
          } />
          {alarm.unreconstructed_reason && (
            <KV k="Reason" v={alarm.unreconstructed_reason} />
          )}
        </dl>
      </div>

      <div>
        <p className="font-semibold text-gray-700 mb-1.5">Spectral evidence</p>
        <dl className="space-y-0.5">
          <KV k="Concentration" v={
            concentration.state === 'not_computed'
              ? 'not yet computed'
              : concentration.ratio !== null
                ? `${(concentration.ratio * 100).toFixed(1)}% (${concentration.band})`
                : 'diffuse — no discrete lines'
          } />
          {/* Harmonic family — canonical peak-anchored */}
          {!harmonics || harmonics.status === 'not_evaluated'
            ? <KV k="Family" v="—" />
            : harmonics.status === 'yes'
              ? <KV k="Family" v={`${harmonics.count}× comb · f₀ ${Math.round(harmonics.family_fundamental_cpm ?? 0)} CPM${harmonics.members ? ` · k∈{${harmonics.members.join(',')}}` : ''}`} />
              : harmonics.status === 'yes_lower_fundamental'
                ? <>
                    <KV k="Family" v={`${harmonics.count}× comb · f₀ ${Math.round(harmonics.family_fundamental_cpm ?? 0)} CPM`} />
                    <KV k="" v={`dominant at k=${harmonics.peak_k} (${Math.round(harmonics.peak_cpm ?? 0)} CPM) · 2f emergence${harmonics.members ? ` · k∈{${harmonics.members.join(',')}}` : ''}`} />
                  </>
                : harmonics.status === 'yes_unrelated'
                  ? <>
                      <KV k="Family" v={`unrelated comb · f₀ ${Math.round(harmonics.family_fundamental_cpm ?? 0)} CPM`} />
                      <KV k="" v="dominant line not a harmonic member — dual finding" />
                    </>
                  : harmonics.rejected_by
                    ? <KV k="Family" v={`no comb (guard ${harmonics.rejected_by})`} />
                    : <KV k="Family" v="no harmonic family" />
          }
          {/* D-03: dominant order from the canonical peak-anchored family
              (identity.dominant_order = harmonics peak_k). No EI roster; null
              renders "—" with the pending-recompute reason. */}
          <KV k="Dominant"
              title={identity.dominant_order === null ? 'order identity pending canonical recompute' : undefined}
              v={identity.dominant_order !== null ? `${Math.round(identity.dominant_order)}×` : '—'} />
          <KV k="RPM tier"  v={identity.rpm_tier ?? '—'} />
          {identity.capture_rpm && (
            <KV k="RPM"     v={`${Math.round(identity.capture_rpm)}`} />
          )}
          {identity.crossover_flag && <KV k="" v="⚠ crossover flag" />}
        </dl>
      </div>

      {/* Alarm Line detail — alarm-anchored (what fired the alarm) */}
      <div>
        <p className="font-semibold text-gray-700 mb-1.5">Alarm Line (RPM-free)</p>
        {!dl || dl.freq_cpm === null ? (
          <p className="text-gray-300 italic">No alarm line resolved</p>
        ) : (
          <dl className="space-y-0.5">
            <KV k="Freq"    v={`${Math.round(dl.freq_cpm)} CPM${dl.alarm_line_label ? ` (${dl.alarm_line_label})` : ''}`} />
            <KV k="Amp"     v={dl.amplitude_ins !== null ? `${dl.amplitude_ins.toFixed(4)} ${SPEC_PK_LABEL}` : '—'} />
            <KV k="Present" v={dl.present_fraction !== null
              ? `dominant in ${Math.round(dl.present_fraction * 100)}% of ${dl.window_days ?? 60}d`
              : '—'} />
            <KV k="ROC"     v={dl.amplitude_roc_ins_per_week !== null
              ? `+${dl.amplitude_roc_ins_per_week.toFixed(5)} ${SPEC_PK_LABEL}/wk`
              : 'stable / declining'} />
            <KV k="Anchor"  v={
              dl.anchor === 'violating'      ? 'violating captures (fired the alarm)'
              : dl.anchor === 'near_fire'    ? 'nearest capture to alarm (linkage unavailable)'
              : dl.anchor === 'latest_capture' ? 'latest capture (not linked to alarm)'
              : '—'
            } />
            {dl.anchor_note && <KV k="Note" v={dl.anchor_note} />}
          </dl>
        )}
        {/* Current spectrum — axis-current peak (fleet tracker), context only */}
        {cs && cs.freq_cpm !== null && (
          <p className="mt-1.5 text-[11px] text-gray-500">
            <span className="font-semibold text-gray-600">Current spectrum:</span>{' '}
            {Math.round(cs.freq_cpm)} CPM
            {cs.amplitude_ins !== null && ` / ${cs.amplitude_ins.toFixed(3)} ${SPEC_PK_LABEL}`}
            {cs.last_seen && ` (${fmtFiredAt(cs.last_seen)})`}
          </p>
        )}
        {/* HF disclosure — above-ceiling energy is reported independently of the
            in-band trended line (never suppressed by it). Redirect to acceleration/PeakVue. */}
        {cs && cs.hf_dominant && cs.hf_freq_cpm !== null && (
          <p className="mt-1 text-[11px] text-orange-600 flex items-start gap-1">
            <span className="font-semibold shrink-0">HF energy:</span>
            <span>
              {(cs.hf_freq_cpm / 1000).toFixed(1)}k CPM
              {cs.hf_amplitude_ins !== null && ` / ${cs.hf_amplitude_ins.toFixed(3)} ${SPEC_PK_LABEL}`}
              {cs.hf_last_seen && ` (${fmtFiredAt(cs.hf_last_seen)})`}
              {' '}— above the velocity tracking ceiling; see acceleration / Impact (PeakVue) for bearing detail.
            </span>
          </p>
        )}
      </div>

      <div>
        <p className="font-semibold text-gray-700 mb-1.5">Per-order ROC</p>
        {/* D-04: velocity_order_roc slopes are UNTRUSTED (pending canonical
            per-order recompute) and are suppressed. The rpm_tier gate still
            takes precedence. An absent slope is honest; a plain slope is not. */}
        {identity.rpm_tier === 'unresolved' ? (
          <p className="text-gray-300 italic">order trends unavailable — running speed unresolved</p>
        ) : (
          <p
            className="text-gray-400 italic cursor-help"
            title="Per-order ROC slopes (velocity_order_roc) are pending canonical recompute and are not validated — suppressed until recomputed on the canonical velocity path."
          >
            pending recompute — not validated
          </p>
        )}
      </div>
    </div>
  );
}

function KV({ k, v, title }: { k: string; v: string; title?: string }) {
  return (
    <div className="flex gap-2">
      {k && <dt className="text-gray-400 shrink-0 w-20">{k}</dt>}
      <dd className={`text-gray-700${title ? ' cursor-help' : ''}`} title={title}>{v}</dd>
    </div>
  );
}
